import { apiCall } from "@/common/api/ApiResult.model";
import { EmployeesHelper } from "@/common/entities/helpers";
import { InformationType, InfoTypeHelper, ListResult } from "@/common/models";
import { SharedState } from "@/common/state";
import { indexBy, path } from "@/fp";
import { to_query_params } from "@/utils/url";
import axios from "axios";
import { MassDownloadPolicyL0StatusItem, MassDownloadUser } from "./model";
import { get_l0_labels_with_policies } from "@/common/api/labels.api";
import moment from "moment";
import { TimeAxisType } from "@/gql/types";

const POLICY_KEY = "dataLeak";
const POLICY_DEFAULT_VALUE = "inactive";

export const get_mass_download_users_for_l0 = apiCall<
  { l0_id: string; date: string; cursor: string; time_type: TimeAxisType },
  ListResult<MassDownloadUser>
>(
  "mass-download.for-l0",
  async ({ l0_id, date, cursor: old_cursor, time_type }) => {
    let qparams;
    if (old_cursor) {
      qparams = {
        cursor: old_cursor,
      };
    } else {
      const time_param = {} as {
        inverseCalendarWeekNumber?: number;
        inverseCalendarMonthNumber?: number;
      };
      if (time_type == TimeAxisType.Month) {
        const time_diff = Math.abs(moment().diff(moment(date), "months"));
        time_param.inverseCalendarMonthNumber = time_diff;
      } else if (time_type == TimeAxisType.Week) {
        const time_diff = Math.abs(moment().diff(moment(date), "days"));
        time_param.inverseCalendarWeekNumber = time_diff < 7 ? 0 : 1;
      }
      //  const inverseMonthNumber = time_diff <= 30 ? 1 : Math.ceil(time_diff / 30);
      qparams = {
        infoTypeCategoryId: l0_id,
        // to get items from the past 7 days
        // inverseWeekNumber:1,
        // to get items from the past 30 days
        ...time_param,
        includeCount: true,
        isScopeMonthly: false,
        limit: 0,
        descentSortByScore: true,
      };
    }

    const res = await axios.get(
      "/extproxy/integrations/anomalous-downloaders?" + to_query_params(qparams)
    );

    const { cursor = "", value } = res.data;

    const employee_ids: string[] = (value.anomalousDownloaders || []).map(
      (item: any) => item.entityId
    );

    let employee_deps_map: Record<string, string> = {};
    if (employee_ids.length > 0) {
      employee_deps_map = await EmployeesHelper.get_employees_departments_name_async(
        employee_ids
      );
    }

    return {
      success: true,
      value: {
        cursor,
        objects: value.anomalousDownloaders.map((val: any) => {
          return {
            id: val.entityId,
            name: val.entityName,
            day: val.dayStartedAt,
            email: val.externalEntityId,
            download_count: val.fileCount,
            department: employee_deps_map[val.entityId] || "",
          } as MassDownloadUser;
        }),
      },
    };
  }
);

export const toggle_l0_mass_download_active_status = apiCall<
  { id: string; activate: boolean; selected_count: number },
  boolean
>(
  "mass-download.change-activate-for-l0",
  async ({ selected_count, activate, id }) => {
    let payload;
    if (selected_count === -1) {
      payload = [
        {
          id,
          policySection: {
            [POLICY_KEY]: {
              policyValue: null,
            },
          },
        },
      ];
    } else {
      const policy_id = activate
        ? `detect_alert_${selected_count}`
        : `detect_${selected_count}`;
      payload = [
        {
          id,
          policySection: {
            [POLICY_KEY]: {
              policyValue: {
                id: policy_id,
              },
            },
          },
        },
      ];
    }

    await axios.patch("/label-api", payload);
    return {
      success: true,
      value: true,
    };
  }
);

const get_policy_detect_count = (p: string): number =>
  +p.split("_").reverse()[0];

export const get_mass_download_info_type_status_list = apiCall<
  void,
  MassDownloadPolicyL0StatusItem[]
>("mass-download.status-list", async () => {
  let last_send_date = "";
  const res_send_date = await get_next_send_date();
  if (res_send_date.success) {
    last_send_date = res_send_date.value;
  }
  const res = await get_l0_labels_with_policies();
  if (res.success) {
    const fresh_l0 = indexBy<InformationType>("id")(res.value);
    console.log("fresh_l0: ", fresh_l0);
    const get_policy = InfoTypeHelper.get_policy(POLICY_KEY);
    return {
      success: true,
      value: SharedState.l0InfoTypes.value
        .filter((f) => f.id !== SharedState.default_label_id.value)
        .map((d) => {
          const policy_id = get_policy(fresh_l0[d.id]) || POLICY_DEFAULT_VALUE;
          return {
            id: d.id,
            name: d.labelName,
            order: d.l0Order,
            next_send_date: last_send_date,
            detect_count:
              policy_id === "inactive"
                ? -1
                : get_policy_detect_count(policy_id),
            is_active: policy_id.includes("alert"),
          } as MassDownloadPolicyL0StatusItem;
        })
        // @ts-ignore
        .sort((a, b) => a.order - b.order),
    };
  } else {
    return { success: true, value: [] };
  }
});

const get_next_send_date_from_path = path(
  "value.maintenanceEntities.$0.nextApproximateUpdateAt",
  () => "?"
);

export const get_next_send_date = apiCall<void, string>(
  "mass-download.next-send-date",
  async () => {
    const qparams = to_query_params({
      scenarioId: "riskyUsers",
      entityType: "org",
      useOrgAsEntity: true,
    });
    const res = await axios.get(
      "/extproxy/integrations/maintenance-entities?" + qparams
    );

    return {
      success: true,
      value: get_next_send_date_from_path(res.data),
    };
  }
);
